import { Link } from "react-router-dom";
import { BRAND, waLink } from "../../brand";
import { ArrowRight, Sparkles, ShieldCheck, Zap } from "lucide-react";

const badges = [
  { label: "+20 automatizaciones", icon: Sparkles },
  { label: "Funnels omnicanal", icon: Zap },
  { label: "IA + Ads + Conversión", icon: ShieldCheck },
];

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 sm:pt-36 lg:pt-40 pb-20 lg:pb-28 overflow-hidden"
    >
      {/* Background atmospherics */}
      <div className="absolute inset-0 bg-radial-orange pointer-events-none" />
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[1100px] h-[1100px] rounded-full opacity-40 pointer-events-none"
           style={{ background: "radial-gradient(closest-side, rgba(255,138,0,0.18), transparent 70%)" }} />
      <div className="absolute inset-0 grain pointer-events-none" />

      <div className="container-x relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* LEFT */}
          <div className="lg:col-span-7 reveal">
            <div className="eyebrow mb-6">
              Consultor · Operador · Implementador
            </div>

            <h1 className="font-display font-extrabold text-white text-[2.5rem] sm:text-[3.25rem] lg:text-[4.25rem] leading-[1.02] tracking-tighter">
              Automatiza tu negocio,{" "}
              <span className="text-gradient text-glow">escala tus ventas</span>{" "}
              y convierte más con IA.
            </h1>

            <p className="mt-7 text-zinc-400 text-lg sm:text-xl max-w-2xl leading-relaxed">
              Implemento sistemas de <span className="text-white">automatización omnicanal</span>,{" "}
              <span className="text-white">chatbots inteligentes</span> y estrategias de adquisición que ayudan
              a empresas a vender más sin perder oportunidades.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Link to="/contacto" className="btn-primary" data-testid="hero-primary-cta">
                Agenda una consultoría
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={waLink()} target="_blank" rel="noreferrer" className="btn-secondary" data-testid="hero-whatsapp-cta">
                <span className="w-2 h-2 rounded-full bg-[#25D366] pulse-dot" />
                Hablar por WhatsApp
              </a>
            </div>

            {/* Authority badges */}
            <div className="mt-10 flex flex-wrap gap-2.5">
              {badges.map(({ label, icon: Icon }) => (
                <div
                  key={label}
                  className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full glass text-xs sm:text-sm text-zinc-300 font-medium"
                >
                  <Icon className="w-3.5 h-3.5 text-[#ff8a00]" />
                  {label}
                </div>
              ))}
            </div>

            {/* Channels strip */}
            <div className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3">
              <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-display">Trabajo con</span>
              {["Google Ads", "Meta Ads", "TikTok Ads", "Respond.io", "Make", "n8n"].map((p) => (
                <span key={p} className="font-display text-zinc-300/90 text-sm tracking-tight">{p}</span>
              ))}
            </div>
          </div>

          {/* RIGHT — Photo + dashboard composition */}
          <div className="lg:col-span-5 relative reveal">
            <div className="relative aspect-[4/5] w-full max-w-md mx-auto">
              {/* Glow halo */}
              <div className="absolute -inset-6 bg-gradient-to-br from-[#ff3b1f]/30 via-[#ff8a00]/20 to-transparent rounded-[2.2rem] blur-2xl" />

              {/* Photo card */}
              <div className="relative h-full w-full rounded-[2rem] overflow-hidden border border-white/10 glow-soft">
                <img
                  src={BRAND.photo}
                  alt="David Osorio - Daos Ads"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="eager"
                />
                {/* Dark gradient blend so the photo merges with the dark UI */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/55 to-[#050505]/10" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/60 via-transparent to-transparent" />

                {/* Dashboard mockup overlay */}
                <div className="absolute -bottom-6 -left-8 w-[78%] rounded-2xl overflow-hidden border border-white/10 glass-strong glow-soft float-soft">
                  <img src={BRAND.images.dashboard} alt="" className="w-full h-auto block opacity-95" />
                </div>

                {/* Stat chip */}
                <div className="absolute top-5 right-5 glass-strong rounded-2xl px-4 py-3 text-xs">
                  <div className="text-zinc-400 font-display tracking-widest uppercase text-[10px]">Conversión</div>
                  <div className="text-white font-display font-bold text-xl">+38.4%</div>
                  <div className="h-1 w-full mt-1 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full bg-gradient-brand" style={{ width: "78%" }} />
                  </div>
                </div>

                {/* Name tag */}
                <div className="absolute bottom-5 right-5 glass rounded-xl px-3.5 py-2.5">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#ff8a00] font-display">David Osorio</div>
                  <div className="text-white text-sm font-display font-semibold">Fundador · Daos Ads</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
